import { useGetTest } from "@/queries/tests/queries";
import { Attachment, PublicQuestion, TestType } from "@/queries/types";
import styled from "@emotion/styled";
import { DSButton, DSModal } from "@hundred5/design-system";
import { t as tCoreMacro } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useRouter } from "next/router";
import { Dispatch } from "react";
import { Answer } from "../../types/test";
import { Action, State } from "./reducer";

interface Props {
  onSubmit: () => void;
  dispatch: Dispatch<Action>;
  state: State;
  questions: PublicQuestion[];
  testType: TestType;
  skipSubmissionSummary?: boolean;
}

export function ConfirmSubmit({
  testType,
  questions,
  skipSubmissionSummary,
  dispatch,
  onSubmit,
  state: { submitting, submitTestModalOpen, answers, attachments },
}: Props) {
  const { t } = useLingui();
  const { query } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const hashedTestId = query.hashedTestId as string;
  const testQuery = useGetTest(jobOpeningSlug, hashedTestId!, !!hashedTestId);
  if (!testQuery.data) {
    return null;
  }
  return (
    <DSModal open={submitTestModalOpen} fluid>
      <Header>
        {!skipSubmissionSummary &&
        !areAllQuestionsAnswered(questions, answers, attachments) ? (
          getTitle(
            testType,
            questions,
            answers,
            attachments,
            skipSubmissionSummary,
          )
        ) : (
          <Title>{t`You're about to submit the test`}</Title>
        )}
      </Header>
      {!skipSubmissionSummary &&
        !areAllQuestionsAnswered(questions, answers, attachments) && (
          <DSModal.Separator></DSModal.Separator>
        )}

      {!skipSubmissionSummary &&
        !areAllQuestionsAnswered(questions, answers, attachments) && (
          <Content>
            <Note>
              <Trans>Skipping the questions may result in a lower grade.</Trans>
            </Note>

            <QuestionsContent>
              {questions.map((question, index) => {
                const number = index + 1;
                const answer = answers[question.test_question_id];
                const liveTestAnswer =
                  testQuery.data?.active_live_test?.answers?.find(
                    (answer) =>
                      answer.question_id === question.test_question_id,
                  );
                const questionAnswered = isQuestionAnswered(
                  question,
                  answers[question.test_question_id],
                );
                const attachmentMissing = isAttachmentRequiredAndMissing(
                  question,
                  attachments[question.test_question_id],
                );

                return (
                  (!questionAnswered || attachmentMissing) && (
                    <QuestionRow key={question.test_question_id}>
                      <QuestionLabel>
                        <Trans>Question</Trans> {number}
                        {attachmentMissing && (
                          <> - {t`Mandatory file missing`}</>
                        )}
                      </QuestionLabel>
                      {(!questionAnswered || attachmentMissing) &&
                      !answer?.skipped ? (
                        <ButtonsWrapper>
                          <DSButton
                            size="xsmall"
                            onClick={() =>
                              dispatch({
                                type: "select_question",
                                payload: {
                                  index,
                                },
                              })
                            }
                          >
                            {attachmentMissing
                              ? t({
                                  message: "Upload",
                                  comment: "Upload file button",
                                })
                              : question.question_type === "video-question"
                                ? t({
                                    message: "Record",
                                    comment: "Record video button",
                                  })
                                : t({
                                    message: "Answer",
                                    comment: "Answer question button",
                                  })}
                          </DSButton>
                          <DSButton
                            variant="secondary"
                            size="xsmall"
                            onClick={() =>
                              dispatch({
                                type: "skip_question",
                                payload: {
                                  questionId: String(question.test_question_id),
                                },
                              })
                            }
                          >
                            <Trans>Skip</Trans>
                          </DSButton>
                        </ButtonsWrapper>
                      ) : null}
                    </QuestionRow>
                  )
                );
              })}
            </QuestionsContent>
          </Content>
        )}
      <Footer>
        <DSButton
          variant="secondary"
          disabled={submitting}
          onClick={() => dispatch({ type: "submit_test_decline" })}
        >
          <Trans>Go Back</Trans>
        </DSButton>
        <DSButton
          data-testid="confirm-submit"
          disabled={
            submitting ||
            (!skipSubmissionSummary &&
              !isReadyForSubmission(questions, answers, attachments))
          }
          onClick={() => onSubmit()}
        >
          {submitting ? t`Submitting...` : t`Submit test`}
        </DSButton>
      </Footer>
    </DSModal>
  );
}

const Content = styled.div`
  padding-top: 16px;
  color: ${(props) => props.theme.colors.purple[100]};

  @media (min-width: 768px) {
    min-width: 572px;
  }
`;

const Header = styled.div`
  font-size: 16px;
  font-weight: 700;
  padding-bottom: 8px;

  @media (min-width: 768px) {
    padding-bottom: 16px;
  }
`;

const Note = styled.div`
  font-size: 14px;
  padding-bottom: 16px;
  color: ${(props) => props.theme.colors.purple[100]};

  @media (min-width: 768px) {
    padding-bottom: 24px;
  }
`;

const Title = styled.div`
  font-weight: 700px;
  font-size: 18px;
`;

const QuestionsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QuestionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QuestionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.purple[100]};
`;

const SkipedQuestion = styled.div`
  color: ${(props) => props.theme.colors.purple[100]};
  font-size: 12px;
  font-weight: 500;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 8px;
  padding-top: 64px;
`;

const isQuestionAnswered = (
  question: PublicQuestion,
  answer: Answer | undefined,
): boolean => {
  if (!answer) {
    return false;
  }

  switch (question.question_type) {
    case "free-text":
    case "code-input":
    case "numeric-input": {
      return !!answer.freeTextAnswer;
    }

    case "picture-question":
    case "single-choice":
    case "multiple-choice": {
      return answer.chosenOptions.length > 0;
    }

    case "video-question": {
      return answer.video !== null;
    }

    default: {
      return false;
    }
  }
};

const isAttachmentRequiredAndMissing = (
  question: PublicQuestion,
  attachments?: Attachment[],
): boolean => {
  if (question.require_attachment !== true) {
    return false;
  }

  if (attachments) {
    return false;
  }

  return true;
};

const isReadyForSubmission = (
  questions: PublicQuestion[],
  answers: Record<string, Answer>,
  attachments: Record<string, Attachment[]>,
): boolean => {
  return questions.reduce<boolean>((ready, question) => {
    const answer = answers[question.test_question_id];
    const attachment = attachments[question.test_question_id];

    const isAnswerReadyForSubmission =
      isQuestionAnswered(question, answer) || answer?.skipped;
    const isAttachmentReadyForSubmission =
      (question.require_attachment === true ? !!attachment : true) ||
      answer?.skipped;

    return (
      isAnswerReadyForSubmission && isAttachmentReadyForSubmission && ready
    );
  }, true);
};

const areAllQuestionsAnswered = (
  questions: PublicQuestion[],
  answers: Record<string, Answer>,
  attachments: Record<string, Attachment[]>,
): boolean => {
  const areAllQuestionsAnswered = questions.reduce<boolean>(
    (ready, question) =>
      isQuestionAnswered(question, answers[question.test_question_id]) && ready,
    true,
  );

  const isAnyMandatoryAttachmentMissing = questions.reduce<boolean>(
    (attachmentMissing, question) =>
      attachmentMissing ||
      (question.require_attachment === true &&
        !attachments[question.test_question_id]),
    false,
  );

  return areAllQuestionsAnswered && !isAnyMandatoryAttachmentMissing;
};

const getTitle = (
  testType: TestType,
  questions: PublicQuestion[],
  answers: Record<string, Answer>,
  attachments: Record<string, Attachment[]>,
  skipSubmissionSummary?: boolean,
): string => {
  let defaultTitle;

  switch (testType) {
    case "video":
      defaultTitle = tCoreMacro`You're about to submit the video intro`;
      break;
    case "quiz":
    case "homework":
    default:
      defaultTitle = tCoreMacro`You missed some questions`;
  }

  if (isReadyForSubmission(questions, answers, attachments)) {
    if (
      areAllQuestionsAnswered(questions, answers, attachments) ||
      skipSubmissionSummary
    ) {
      return defaultTitle;
    } else {
      switch (testType) {
        case "video":
          return tCoreMacro`Are you sure you want to submit the video intro with missing answer(s)?`;
        case "homework":
        case "quiz":
        default:
          return tCoreMacro`You missed some questions`;
      }
    }
  } else {
    return defaultTitle;
  }
};
